import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { DEFAULT_TERMS, DEFAULT_COOKIES } from '../../lib/pageDefaults';
import { getDefaultManual } from '../../pages/UserManualPage';

const PAGES = {
  terms: {
    label: 'Terms of Service',
    help: 'Edit the public Terms of Service page. Leave a blank line between paragraphs. Sections are numbered automatically.',
    defaults: () => DEFAULT_TERMS,
    path: '/terms',
  },
  cookies: {
    label: 'Cookie Policy',
    help: 'Edit the public Cookie Policy page. Leave a blank line between paragraphs. Sections are numbered automatically.',
    defaults: () => DEFAULT_COOKIES,
    path: '/cookies',
  },
  // manual: {
  //   label: 'Help Manual',
  //   help: 'Edit the public Help Manual (/help). Each section becomes a topic in the sidebar. Formatting: blank line between paragraphs; "## " starts a sub-heading; lines starting "- " make a bullet list; lines starting "1. ", "2. " make numbered steps; "> " makes a highlighted tip.',
  //   defaults: () => getDefaultManual(),
  //   path: '/help',
  // },
};

export default function LegalContentEditor() {
  const { api } = useAuth();
  const [pageKey, setPageKey] = useState('terms');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const cfg = PAGES[pageKey];

  useEffect(() => {
    let cancelled = false;
    setForm(null);
    api.get(`/pages/${pageKey}`)
      .then(res => {
        if (cancelled) return;
        setForm(Array.isArray(res.data?.sections)
          ? { title: res.data.title || cfg.defaults().title, last_updated: res.data.last_updated || '', sections: res.data.sections }
          : cfg.defaults());
      })
      .catch(() => { if (!cancelled) setForm(cfg.defaults()); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${pageKey}`, form); toast.success(`${cfg.label} saved`); }
    catch { toast.error(`Failed to save ${cfg.label}`); }
    finally { setSaving(false); }
  };
  const update = (i, patch) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const move = (i, dir) =>
    setForm(f => {
      const arr = [...f.sections]; const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, sections: arr };
    });

  return (
    <div className="space-y-6 pt-6 border-t">
      <div>
        <h2 className="text-xl font-semibold">Other Legal &amp; Help Pages</h2>
        <p className="text-sm text-muted-foreground mt-1">{cfg.help}</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {Object.entries(PAGES).map(([k, p]) => (
          <Button key={k} variant={k === pageKey ? 'default' : 'outline'} size="sm"
            className={k === pageKey ? 'bg-[#003366] hover:bg-[#003366]/90' : ''} onClick={() => setPageKey(k)}>
            {p.label}
          </Button>
        ))}
        <a href={cfg.path} target="_blank" rel="noreferrer" className="text-sm text-[#003366] underline self-center ml-2">View public page</a>
      </div>

      {!form ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <>
          <Card>
            <CardContent className="pt-6 grid gap-4 md:grid-cols-2">
              <div className="space-y-2"><Label>Page Title</Label><Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Last Updated</Label><Input value={form.last_updated || ''} onChange={e => setForm(f => ({ ...f, last_updated: e.target.value }))} placeholder="e.g. 20 October 2026" /></div>
            </CardContent>
          </Card>
          {form.sections.map((sec, i) => (
            <Card key={i}>
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-muted-foreground w-6">{i + 1}.</span>
                  <Input value={sec.heading} onChange={e => update(i, { heading: e.target.value })} placeholder="Section heading" />
                  <Button variant="outline" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
                  <Button variant="outline" size="sm" onClick={() => move(i, 1)} disabled={i === form.sections.length - 1}>↓</Button>
                  <Button variant="outline" size="sm" className="text-red-600" onClick={() => setForm(f => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }))}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <Textarea value={sec.body} onChange={e => update(i, { body: e.target.value })} rows={pageKey === 'manual' ? 14 : 8} placeholder="Section text" />
              </CardContent>
            </Card>
          ))}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setForm(f => ({ ...f, sections: [...f.sections, { heading: '', body: '' }] }))}><Plus className="w-4 h-4 mr-2" />Add Section</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { if (window.confirm('Reset the editor to the original default text? (Not saved until you click Save.)')) setForm(cfg.defaults()); }}>Reset to Default</Button>
              <Button onClick={save} disabled={saving} className="bg-[#003366] hover:bg-[#003366]/90 px-8">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save {cfg.label}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
